import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'

import lang from '@/lang'
import { TextInput } from '@/modules/base/components/inputs'
import checkValidPhoneNumber from '@/modules/onboarding/components/_helpers/checkValidPhoneNumber'
import { setCompanyData } from '@/redux/ducks/onboarding/main-v2'
import { isMeinBuero } from '@/utils/build-env'
import validateInputs from '@/modules/onboardingV2/helpers/meinbuero/validateInputs'
import InputNames from '@/modules/onboardingV2/constants/input-names'

const { taxConsultantUser: { inputs: inputTexts } } = lang.onboardingV2

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
  column: {
    width: '100%',
  },
  multiRow: {
    width: '100%',
    '& + $name': {
      marginTop: 20,
      '@media (min-width: 800px)': {
        marginTop: 0,
      },
    },
    '@media (min-width: 800px)': {
      width: 'calc(50% - 10px)',
    },
  },
  inputsRow: {
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',

    [theme.devices.mobile]: {
      flexDirection: 'column',
      gap: 20,
    },
  },
}))

const TaxConsultantUserInputs = props => {
  const { setInputsValid, setPhoneValid } = props
  const classes = useStyles()
  const dispatch = useDispatch()
  const companyData = useSelector(state => state.onboarding.mainV2.companyData)
  const email = useSelector(state => state.user.info.email)
  const [errorMessages, setErrorMessages] = useState({ lastName: '', firstName: '', phone1: '' })

  const validateInput = (name, value) => {
    if (!isMeinBuero()) { return }

    const updatedErrorMessage = { ...errorMessages }
    updatedErrorMessage[name] = validateInputs(name, value)

    setErrorMessages(updatedErrorMessage)
  }

  useEffect(() => {
    const {
      firstName,
      lastName,
      phone1,
    } = companyData

    if (isMeinBuero()) {
      const firstNameValid = firstName && !errorMessages.firstName
      const lastNameValid = lastName && !errorMessages.lastName
      const phoneValid = phone1 && !errorMessages.phone1
      setInputsValid(firstNameValid && lastNameValid && phoneValid)
      setPhoneValid(phoneValid)
      return
    }

    setInputsValid(firstName && lastName)
    setPhoneValid(checkValidPhoneNumber(phone1))
  }, [companyData])

  const handleInputChange = name => value => {
    validateInput(name, value)
    dispatch(setCompanyData({ ...companyData, [name]: value }))
  }

  return (
    <>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <TextInput
              name={InputNames.firstName}
              className={classes.multiRow}
              label={inputTexts.firstName}
              required
              errorMessage={errorMessages.firstName}
              value={companyData.firstName}
              onChange={handleInputChange(InputNames.firstName)}
            />
            <TextInput
              name={InputNames.lastName}
              className={classes.multiRow}
              label={inputTexts.lastName}
              required
              value={companyData.lastName}
              errorMessage={errorMessages.lastName}
              onChange={handleInputChange(InputNames.lastName)}
            />
          </div>
        </div>
      </div>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <TextInput
              name={InputNames.email}
              label={inputTexts.email}
              required
              readOnly
              value={email}
            />
          </div>
        </div>
      </div>
      <div className={classes.root}>
        <div className={classes.column}>
          <div className={classes.inputsRow}>
            <TextInput
              name={InputNames.phone1}
              label={inputTexts.phone}
              required
              value={companyData.phone1}
              errorMessage={errorMessages.phone1}
              onChange={handleInputChange(InputNames.phone1)}
            />
          </div>
        </div>
      </div>
    </>
  )
}

TaxConsultantUserInputs.propTypes = {
  setInputsValid: PropTypes.func,
  setPhoneValid: PropTypes.func,
}

TaxConsultantUserInputs.defaultProps = {
  setInputsValid: () => {},
  setPhoneValid: () => {},
}

export default TaxConsultantUserInputs
